import { VisualFieldRoleType } from 'src/app/pbir/visuals/VisualFieldRoleType';

export enum PbiChartDataRoleKey {
  Rows = 'rows',
  Columns = 'columns',
  Values = 'values'
};

export interface PbiChartDataRoles extends Record<PbiChartDataRoleKey, PbiDataRole | null>, Record<string, PbiDataRole | null> {
  rows: PbiDataRole | null;
  columns: PbiDataRole | null;
  values: PbiDataRole | null;
};

export interface PbiChartDefaultDataRoles {
  measuresDataRole?: string;
  fallbackDataRole: string;
};


export interface PbiChartDataRoleOptions {
  maxFields?: number;
  replace?: boolean;
  disabled?: boolean;
  disabledMessage?: string;
  fieldCountForScroll?: number;
};

export interface VisualChartRoleOptionsMap extends Record<string, PbiChartDataRoleOptions> {
  rows?: PbiChartDataRoleOptions;
  columns?: PbiChartDataRoleOptions;
  values?: PbiChartDataRoleOptions;
};

export interface VisualChartGetRoleOptions extends Record<string, { fieldCount?: number }> {
  rows?: {
    fieldCount?: number
  };
  columns?: {
    fieldCount?: number
  };
  values?: {
    fieldCount?: number
  };
};


export enum PbiDataRoleAggregation {
  Disabled,
  Allowed,
  Required,
};

export enum VisualChartRoleCategorisation {
  /* For charts that support role categorisation, e.g. pivot table, bar chart */
  Categorised,

  /* For charts that do not support role categorisation, e.g. card, table */
  Uncategorised
};

export interface PbiDataRole {
  name: string;

  title: string;
  singularTitle: string;
  icon: string;
  svgIcon: string;
  description: string;

  aggregation: PbiDataRoleAggregation;

  kind: VisualFieldRoleType.Kind;
};

export const PbiDataRoles = {
  unknown: <PbiDataRole>{
    name: '',

    title: '',
    icon: '',
    description: '<Invalid data role>',

    aggregation: PbiDataRoleAggregation.Disabled,

    kind: VisualFieldRoleType.Kind.Values
  },  


  chartAxis: <PbiDataRole> {
    name: 'Category',

    title: 'Axis',
    icon: '',
    description: 'Fields to place on the horizontal or vertical axis',

    aggregation: PbiDataRoleAggregation.Disabled,

    kind: VisualFieldRoleType.Kind.Grouping
  },

  chartLegend: <PbiDataRole>{
    name: 'Series',

    title: 'Legend',
    icon: '',
    description: 'The categorical field to show for color',

    aggregation: PbiDataRoleAggregation.Disabled,

    kind: VisualFieldRoleType.Kind.Detail,
  },

  chartMeasures: <PbiDataRole>{
    name: 'Y',

    title: 'Measures', //'Values',
    singularTitle: 'Measure',
    icon: '',
    description: 'The numeric amounts to plot',

    aggregation: PbiDataRoleAggregation.Required,

    kind: VisualFieldRoleType.Kind.Values
  },

  chartSmallMultiples: <PbiDataRole>{
    name: 'Rows',

    title: 'Small multiples',
    singularTitle: 'Small multiple',
    icon: '',
    description: 'Split your visual into multiple, smaller visuals based on your selected fields',

    aggregation: PbiDataRoleAggregation.Disabled,

    kind: VisualFieldRoleType.Kind.Detail,
  },

  chartTooltips: <PbiDataRole>{
    name: 'Tooltips',

    title: 'Tooltips',
    singularTitle: 'Tooltip',
    icon: '',
    description: 'Add additional data fields that people can see in the tooltip when they hover on part of this visual',

    aggregation: PbiDataRoleAggregation.Disabled,

    kind: VisualFieldRoleType.Kind.Tooltip
  },


  pivotTableRows: <PbiDataRole>{
    name: 'Rows',

    title: 'Rows',
    singularTitle: 'Row',
    icon: '',
    description: 'The fields to use on rows',

    aggregation: PbiDataRoleAggregation.Disabled,

    kind: VisualFieldRoleType.Kind.Grouping
  },

  pivotTableColumns: <PbiDataRole>{
    name: 'Columns',

    title: 'Columns',
    singularTitle: 'Column',
    icon: '',
    description: 'The fields to use on columns',

    aggregation: PbiDataRoleAggregation.Disabled,

    kind: VisualFieldRoleType.Kind.Detail
  },

  pivotTableMeasures: <PbiDataRole>{
    name: 'Values',

    title: 'Measures',
    singularTitle: 'Measure',
    icon: '',
    description: '',

    aggregation: PbiDataRoleAggregation.Required,

    kind: VisualFieldRoleType.Kind.Values
  },


  tableFields: <PbiDataRole>{
    name: 'Values',

    title: 'Fields',
    singularTitle: 'Field',
    icon: '',
    description: '',

    aggregation: PbiDataRoleAggregation.Allowed,

    kind: VisualFieldRoleType.Kind.Values
  },


  pieDonutDetails: <PbiDataRole>{
    name: 'Series',

    title: 'Details',
    singularTitle: 'Detail',
    icon: '',
    description: 'Each slice is split by this categorical field',

    aggregation: PbiDataRoleAggregation.Disabled,

    kind: VisualFieldRoleType.Kind.Detail
  },
  
  pieDonutLegend: <PbiDataRole>{
    name: 'Category',

    title: 'Legend',
    icon: '',
    description: 'The categorical field to show for color',

    aggregation: PbiDataRoleAggregation.Disabled,

    kind: VisualFieldRoleType.Kind.Grouping
  },

  pieDonutMeasures: <PbiDataRole>{
    name: 'Y',

    title: 'Measures',//'Values',
    singularTitle: 'Measure',
    icon: '',
    description: 'The numeric amounts to plot',

    aggregation: PbiDataRoleAggregation.Required,

    kind: VisualFieldRoleType.Kind.Values
  },


  cardFields: <PbiDataRole>{
    name: 'Values',

    title: 'Fields',
    singularTitle: 'Field',
    icon: '',
    description: 'The fields to display',

    aggregation: PbiDataRoleAggregation.Required,

    kind: VisualFieldRoleType.Kind.Values
  },  


  treemapDetails: <PbiDataRole>{
    name: 'Details',

    title: 'Details',
    singularTitle: 'Detail',
    icon: '',
    description: '',

    aggregation: PbiDataRoleAggregation.Disabled,

    kind: VisualFieldRoleType.Kind.Detail
  },
  
  treemapGroup: <PbiDataRole>{
    name: 'Group',

    title: 'Group',
    icon: '',
    description: 'A rectangle of relative size is created for each value in this field',

    aggregation: PbiDataRoleAggregation.Disabled,

    kind: VisualFieldRoleType.Kind.Grouping
  },

  treemapMeasures: <PbiDataRole>{
    name: 'Values',

    title: 'Measures', //Values
    singularTitle: 'Measure',
    icon: '',
    description: 'The numeric amounts to plot',

    aggregation: PbiDataRoleAggregation.Required,

    kind: VisualFieldRoleType.Kind.Values
  },


  waterfallBreakdown: <PbiDataRole> {
    name: 'Breakdown',

    title: 'Breakdown',
    icon: '',
    description: 'Explore change between categories based on this field',

    aggregation: PbiDataRoleAggregation.Disabled,

    kind: VisualFieldRoleType.Kind.Detail
  },

  waterfallCategory: <PbiDataRole>{
    name: 'Category',

    title: 'Category',
    icon: '',
    description: 'Each field is a stage in the waterfall',

    aggregation: PbiDataRoleAggregation.Disabled,

    kind: VisualFieldRoleType.Kind.Grouping
  },
};

export function findPbiDataRoleByName(name: string): PbiDataRole {
  name = name.toLowerCase();

  for (let k in PbiDataRoles) {
    let roles = (PbiDataRoles as any)[k];

    if (roles.name.toLowerCase() === name) {
      return roles;
    }
  }

  return null;
}

export function isDataRole(dataRoleOrName: PbiDataRole | string, targetDataRole: PbiDataRole) {
  let name = (typeof dataRoleOrName !== 'string') ? dataRoleOrName.name : dataRoleOrName;

  name = name.toLowerCase();

  return (name == targetDataRole.name.toLowerCase());
}
